import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
  updateEntityListDataMultipleFromUpdatedEntityData,
  addNewEntityDataToEntityListData,
} from '../../modules/mockDataCommon'
import { getOrderMasterServiceListData } from '../orderMasterServices/mockData'
import { getOrderMSServiceListData } from '../orderMSServices/mockData'
import { getContractListData } from '../contracts/mockData'
import { getOrderServiceOrderListDataByOrderId } from '../orderServiceOrders/mockData'
import { getOrderLastSelectedStateDataByOrderId } from '../orderLastSelectedStates/mockData'
import { getTaskListDataByOrderId } from '../tasks/mockData'
import { getCallListDataByOrderId } from '../calls/mockData'
import { getEmailListDataByOrderId } from '../emails/mockData'
import { requireAuthenticatedUser } from '../../decorators'

import { createMockOperation } from '../operations'
import {
  getOrderListData,
  getOrderOverviewData,
  getSupplierListDataByOrderId,
} from './mockData'
import { assertIsNotUndefined } from '~/types/models'
import type { IEntitySelectItemWithOptionalAvatar } from '~/types/models'
import type { THridAndVersion } from '~/api/msw/modules/mockDataCommon'
import type {
  IOrder,
  IOrderLastSelectedState,
  IOrderOverview,
  IOrderOverviewForPost,
  IOrderMSService,
  IOrderMasterService,
} from '~/types/order/models'

interface GetordersResponse {
  data: IOrder[]
}

async function getorders(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const responseBody: GetordersResponse = {
    data: await getOrderListData(),
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

async function getOrder(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  const list = await getOrderListData()
  if (!list) {
    return res(ctx.status(404), ctx.json({ message: 'Resource not found.' }))
  }
  const order = list.find((obj) => obj.id === id)
  if (order) {
    const responseBody = { data: order }
    return res(ctx.status(200), ctx.json(responseBody))
  } else {
    return res(ctx.status(404), ctx.json({ message: 'Resource not found.' }))
  }
}

interface GetOrderOverviewResponse {
  data: IOrderOverview
}

async function getOrderOverviewById(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetOrderOverviewResponse = {
      data: await getOrderOverviewData(id)!, // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't display Order ${id} Overview: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

interface GetOrderLastSelectedStateResponse {
  data: IOrderLastSelectedState
}

async function getOrderLastSelectedStateById(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetOrderLastSelectedStateResponse = {
      data: await getOrderLastSelectedStateDataByOrderId(id)!, // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't get Order ${id} last selected data: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

async function getOrderServiceOrdersByOrderId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning orderServiceOrders for order "${id}".`)
  const data = await getOrderServiceOrderListDataByOrderId(id)
  return res(ctx.status(200), ctx.json({ data }))
}

async function getTasksByOrderId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning tasks for order "${id}".`)
  const data = await getTaskListDataByOrderId(id)
  return res(ctx.status(200), ctx.json({ data }))
}

async function getCallsByOrderId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning calls for order "${id}".`)
  const data = await getCallListDataByOrderId(id)
  return res(ctx.status(200), ctx.json({ data }))
}

async function getEmailsByOrderId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning emails for order "${id}".`)
  const data = await getEmailListDataByOrderId(id)
  return res(ctx.status(200), ctx.json({ data }))
}

// post

const postOrder = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return addToEntityListData(req, res, ctx, getOrderListData)
}

async function postOrderOverview(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  type PostResponse = THridAndVersion

  const fn = 'postOrderOverview |'
  const overviewData: IOrderOverviewForPost = await req.json()
  console.debug(`${fn} overviewData:`, overviewData)

  // order
  assertIsNotUndefined(overviewData.order.id) // Should never be. (This is a type guard.)
  const preexistingOrderOverviewData = await getOrderOverviewData(
    overviewData.order.id
  )
  console.debug(`${fn} preexising orderOverview:`, preexistingOrderOverviewData)
  let orderHridAndVersion = undefined as unknown as THridAndVersion
  if (preexistingOrderOverviewData) {
    orderHridAndVersion = {
      hrid: preexistingOrderOverviewData.order.hrid,
      version: preexistingOrderOverviewData.order.version,
    }
    console.debug(
      `${fn} The order in this IOrderOverviewForPost already exists. Not adding.`
    )
  } else {
    console.debug(`${fn} Adding order to entityListData`)
    orderHridAndVersion = await addNewEntityDataToEntityListData({
      req,
      newEntityData: overviewData.order,
      getEntityListDataFn: getOrderListData,
    })
  }

  // orderMSServices (has to preceed orderMasterServices
  // cause orderMSServices references them)
  const allMSServices: Partial<IOrderMSService>[] =
    overviewData.orderMasterServices
      .filter((ms: Partial<IOrderMasterService>) => !!ms.orderMSServices)
      .map((ms: Partial<IOrderMasterService>) => ms.orderMSServices)
      .flat()
  const msServicesHridArray = await addNewEntityDataToEntityListData({
    req,
    entityKeyBaseOverride: 'ordermsservices',
    newEntityData: allMSServices,
    getEntityListDataFn: getOrderMSServiceListData,
  })

  // orderMasterServices
  const orderMasterServicesToAdd = overviewData.orderMasterServices.filter(
    (moms) =>
      !preexistingOrderOverviewData?.orderMasterServices.some(
        (pemoms) => pemoms.id === moms.id
      )
  )
  const masterServicesHridArray = await addNewEntityDataToEntityListData({
    req,
    entityKeyBaseOverride: 'ordermasterservices',
    newEntityData: orderMasterServicesToAdd,
    getEntityListDataFn: getOrderMasterServiceListData,
  })

  // contracts (update: add order.id )
  await updateEntityListDataMultipleFromUpdatedEntityData({
    req, // request is made to the orders api, so request contains order.id
    entityKeyBaseOverride: 'contracts',
    updatedEntityDataMultiple: overviewData.contracts,
    getEntityListDataFn: getContractListData,
  })

  const responseBody: PostResponse = orderHridAndVersion // TEMP WHILE IMPLEMENTING
  const idempotencyKey = req.headers.get('Idempotency-Key')
  if (idempotencyKey) {
    const operationId = createMockOperation(
      'POST /api/orders/overview',
      responseBody
    )
    return res(
      ctx.status(202),
      ctx.json({
        data: {
          operation_id: operationId,
          status: 'pending',
          operation_type: 'POST /api/orders/overview',
        },
      })
    )
  }
  return res(ctx.status(201), ctx.json(responseBody))
}

const patchOrder = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getOrderListData)
}

const deleteOrder = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(req, res, ctx, getOrderListData)
}

interface GetSuppliersFromOrderResponse {
  data: IEntitySelectItemWithOptionalAvatar[]
}

const getSuppliersFromOrder = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const { id } = req.params
  if (typeof id !== 'string') {
    return res(
      ctx.status(500),
      ctx.json({ statusCode: 500, message: 'Passed ID is not a string.' })
    )
  }
  const onlyWithReadyForContractMSServices =
    req.url.searchParams.get('onlywithreadyforcontractmsservices') === 'true'
  try {
    const responseBody: GetSuppliersFromOrderResponse = {
      data: await getSupplierListDataByOrderId(
        id,
        onlyWithReadyForContractMSServices
      ),
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    return res(
      ctx.status(404),
      ctx.json({
        statusCode: 404,
        message: `Can't get Suppliers from order ${id}: ${
          (e as Error).message
        }`,
      })
    )
  }
}

export const handlers = [
  {
    method: 'get',
    path: '/api/orders',
    handler: getorders,
  },
  {
    method: 'get',
    path: '/api/orders/:id/suppliers',
    handler: getSuppliersFromOrder,
  },
  {
    method: 'get',
    path: '/api/orders/:id',
    handler: getOrder,
  },
  {
    method: 'get',
    path: '/api/orders/:id/overview',
    handler: getOrderOverviewById,
  },
  {
    method: 'get',
    path: '/api/orders/:id/lastselectedstate',
    handler: getOrderLastSelectedStateById,
  },
  {
    method: 'get',
    path: '/api/orders/:id/orderserviceorders',
    handler: getOrderServiceOrdersByOrderId,
  },
  {
    method: 'get',
    path: '/api/orders/:id/tasks',
    handler: getTasksByOrderId,
  },
  {
    method: 'get',
    path: '/api/orders/:id/calls',
    handler: getCallsByOrderId,
  },
  {
    method: 'get',
    path: '/api/orders/:id/emails',
    handler: getEmailsByOrderId,
  },
  {
    method: 'post',
    path: '/api/orders',
    handler: postOrder,
  },
  {
    method: 'post',
    path: '/api/orders/overview',
    handler: postOrderOverview,
  },
  {
    method: 'patch',
    path: '/api/orders/:id',
    handler: patchOrder,
  },
  {
    method: 'delete',
    path: '/api/orders/:id',
    handler: deleteOrder,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
